import { parseDice } from './dice.ts';
import { evaluateFormula, parseFormulaCached } from './formula.ts';
import type { FieldConfig, FieldDef, FieldType, FileMeta, FilterOp, RecordData, SelectOption } from './model.ts';

/* ----------------------------------------------------------------------------------------------
 * Field type registry
 * -------------------------------------------------------------------------------------------- */

export interface FieldTypeInfo {
  type: FieldType;
  label: string;
  /** Icon token understood by the web app's icon map. */
  icon: string;
  description: string;
  /** Can users add this type from the field menu? */
  creatable: boolean;
  /** Can a board be grouped by it? */
  groupable: boolean;
  /** Numeric-ish: usable in formulas, charts and number aggregates. */
  numeric: boolean;
  filterOps: FilterOp[];
}

const EMPTY_OPS: FilterOp[] = ['isEmpty', 'isNotEmpty'];
const TEXT_OPS: FilterOp[] = ['contains', 'notContains', 'is', 'isNot', ...EMPTY_OPS];
const NUM_OPS: FilterOp[] = ['is', 'isNot', 'gt', 'gte', 'lt', 'lte', ...EMPTY_OPS];
const LIST_OPS: FilterOp[] = ['hasAny', 'hasAll', 'hasNone', ...EMPTY_OPS];

export const FIELD_TYPES: Record<FieldType, FieldTypeInfo> = {
  text: { type: 'text', label: 'Text', icon: 'text', description: 'A line of text', creatable: true, groupable: false, numeric: false, filterOps: TEXT_OPS },
  number: { type: 'number', label: 'Number', icon: 'hash', description: 'Stats, costs, timings — optionally shown as a bar', creatable: true, groupable: false, numeric: true, filterOps: NUM_OPS },
  select: { type: 'select', label: 'Select', icon: 'circle-dot', description: 'One option from a colored list', creatable: true, groupable: true, numeric: false, filterOps: ['is', 'isNot', ...EMPTY_OPS] },
  multiSelect: { type: 'multiSelect', label: 'Tags', icon: 'tags', description: 'Any number of colored tags', creatable: true, groupable: false, numeric: false, filterOps: LIST_OPS },
  checkbox: { type: 'checkbox', label: 'Checkbox', icon: 'check-square', description: 'Yes or no', creatable: true, groupable: true, numeric: true, filterOps: ['checked', 'unchecked'] },
  date: { type: 'date', label: 'Date', icon: 'calendar', description: 'A calendar date', creatable: true, groupable: false, numeric: false, filterOps: ['is', 'before', 'after', ...EMPTY_OPS] },
  person: { type: 'person', label: 'Person', icon: 'user', description: 'Teammates — owners, assignees, reviewers', creatable: true, groupable: true, numeric: false, filterOps: LIST_OPS },
  relation: { type: 'relation', label: 'Relation', icon: 'link', description: 'Links to other records (abilities of a unit, art of a card…)', creatable: true, groupable: false, numeric: false, filterOps: LIST_OPS },
  url: { type: 'url', label: 'URL', icon: 'globe', description: 'A web link', creatable: true, groupable: false, numeric: false, filterOps: TEXT_OPS },
  rating: { type: 'rating', label: 'Rating', icon: 'star', description: '1–5 stars (fun, priority, confidence…)', creatable: true, groupable: false, numeric: true, filterOps: NUM_OPS },
  range: { type: 'range', label: 'Range', icon: 'move-horizontal', description: 'A min–max pair, e.g. damage 40–60', creatable: true, groupable: false, numeric: true, filterOps: NUM_OPS },
  dice: { type: 'dice', label: 'Dice', icon: 'dices', description: 'Dice notation like 2d6+3, with min/avg/max', creatable: true, groupable: false, numeric: true, filterOps: NUM_OPS },
  color: { type: 'color', label: 'Color', icon: 'palette', description: 'A hex color swatch', creatable: true, groupable: false, numeric: false, filterOps: EMPTY_OPS },
  formula: { type: 'formula', label: 'Formula', icon: 'sigma', description: 'Computed from other fields, e.g. {Damage} * {Attack speed}', creatable: true, groupable: false, numeric: true, filterOps: NUM_OPS },
  votes: { type: 'votes', label: 'Votes', icon: 'thumbs-up', description: 'Everyone can up-vote records', creatable: true, groupable: false, numeric: true, filterOps: NUM_OPS },
  file: { type: 'file', label: 'Files', icon: 'paperclip', description: 'Images, audio and reference attached to the record', creatable: true, groupable: false, numeric: false, filterOps: EMPTY_OPS },
};

export const CREATABLE_FIELD_TYPES = (Object.values(FIELD_TYPES) as FieldTypeInfo[]).filter((t) => t.creatable).map((t) => t.type);

/* ----------------------------------------------------------------------------------------------
 * Resolution context — lets pure helpers turn ids into titles/names without knowing the store.
 * -------------------------------------------------------------------------------------------- */

export interface ValueContext {
  recordTitle?(id: string): string | undefined;
  personName?(id: string): string | undefined;
  /** Fields of the record's collection (needed for formulas). */
  fields?: readonly FieldDef[];
}

/* ----------------------------------------------------------------------------------------------
 * Value shapes
 * -------------------------------------------------------------------------------------------- */

export const asString = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
export const asNumber = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return null;
};
export const asStringArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : typeof v === 'string' && v ? [v] : [];
export const asRange = (v: unknown): [number | null, number | null] => {
  if (Array.isArray(v)) return [asNumber(v[0]), asNumber(v[1])];
  const n = asNumber(v);
  return [n, n];
};
export const asFile = (v: unknown): FileMeta | null =>
  v && typeof v === 'object' && typeof (v as FileMeta).hash === 'string' ? (v as FileMeta) : null;

/** A file field holds a list. A lone file, as asset libraries used to store, reads as one item. */
export const asFiles = (v: unknown): FileMeta[] => {
  if (Array.isArray(v)) return v.map(asFile).filter((f): f is FileMeta => f !== null);
  const one = asFile(v);
  return one ? [one] : [];
};

export function selectOption(field: FieldDef, id: unknown): SelectOption | undefined {
  if (typeof id !== 'string') return undefined;
  return field.config.options?.find((o) => o.id === id);
}

export function voteScore(record: RecordData): number {
  let s = 0;
  for (const v of Object.values(record.votes)) s += v;
  return s;
}

/** Raw stored value of a field (formula/votes are computed). */
export function rawValue(record: RecordData, field: FieldDef): unknown {
  return record.values[field.id];
}

export function isEmptyValue(field: FieldDef, value: unknown): boolean {
  switch (field.type) {
    case 'checkbox':
      return value !== true;
    case 'multiSelect':
    case 'person':
    case 'relation':
      return asStringArray(value).length === 0;
    case 'range': {
      const [a, b] = asRange(value);
      return a === null && b === null;
    }
    case 'number':
    case 'rating':
      return asNumber(value) === null;
    case 'file':
      return asFiles(value).length === 0;
    default:
      return value === undefined || value === null || value === '';
  }
}

/* ----------------------------------------------------------------------------------------------
 * Numbers
 * -------------------------------------------------------------------------------------------- */

/** Numeric reading of any field, used by formulas, sorting, aggregates and stat bars. */
export function numericValue(record: RecordData, field: FieldDef, ctx: ValueContext = {}, depth = 0): number | null {
  const v = record.values[field.id];
  switch (field.type) {
    case 'number':
    case 'rating':
      return asNumber(v);
    case 'checkbox':
      return v === true ? 1 : 0;
    case 'range': {
      const [a, b] = asRange(v);
      if (a === null && b === null) return null;
      return ((a ?? b)! + (b ?? a)!) / 2;
    }
    case 'dice': {
      const d = parseDice(asString(v));
      return d ? d.avg : null;
    }
    case 'votes':
      return voteScore(record);
    case 'formula':
      return formulaValue(record, field, ctx, depth);
    case 'multiSelect':
    case 'person':
    case 'relation':
      return asStringArray(v).length || null;
    case 'text':
    case 'url':
      return asNumber(v);
    default:
      return null;
  }
}

export function formulaValue(record: RecordData, field: FieldDef, ctx: ValueContext, depth = 0): number | null {
  const expression = field.config.expression ?? '';
  if (!expression.trim() || depth > 8) return null;
  const ast = parseFormulaCached(expression);
  if (ast instanceof Error) return null;
  const fields = ctx.fields ?? [];
  const res = evaluateFormula(ast, (ref) => {
    const target = fields.find((f) => f.id === ref);
    if (!target || target.id === field.id) return null;
    return numericValue(record, target, ctx, depth + 1);
  });
  return res.ok ? res.value : null;
}

export function formulaError(field: FieldDef, fields: readonly FieldDef[]): string | null {
  const expression = field.config.expression ?? '';
  if (!expression.trim()) return null;
  const ast = parseFormulaCached(expression);
  if (ast instanceof Error) return ast.message;
  const unknown = [...expression.matchAll(/\{([^}]*)\}/g)].map((m) => m[1]!).filter((id) => !fields.some((f) => f.id === id));
  return unknown.length ? 'References a field that no longer exists' : null;
}

export function formatNumber(n: number | null | undefined, config: FieldConfig = {}): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '';
  const decimals = config.decimals;
  const text =
    decimals === undefined
      ? Number.isInteger(n)
        ? n.toLocaleString('en-US')
        : n.toLocaleString('en-US', { maximumFractionDigits: 2 })
      : n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return `${config.prefix ?? ''}${text}${config.suffix ?? ''}`;
}

/* ----------------------------------------------------------------------------------------------
 * Text rendering (search, CSV/JSON export, sorting of non-numeric values)
 * -------------------------------------------------------------------------------------------- */

export function valueToText(record: RecordData, field: FieldDef, ctx: ValueContext = {}): string {
  const v = record.values[field.id];
  switch (field.type) {
    case 'text':
    case 'url':
    case 'color':
      return asString(v);
    case 'dice': {
      const s = asString(v);
      const d = parseDice(s);
      return d ? `${s} (avg ${formatNumber(d.avg, { decimals: Number.isInteger(d.avg) ? 0 : 1 })})` : s;
    }
    case 'number':
      return formatNumber(asNumber(v), field.config);
    case 'rating': {
      const n = asNumber(v);
      return n ? `${n}/${field.config.max ?? 5}` : '';
    }
    case 'range': {
      const [a, b] = asRange(v);
      if (a === null && b === null) return '';
      if (a === b || b === null) return formatNumber(a, field.config);
      if (a === null) return formatNumber(b, field.config);
      return `${formatNumber(a, { ...field.config, suffix: '' })}–${formatNumber(b, field.config)}`;
    }
    case 'checkbox':
      return v === true ? 'Yes' : 'No';
    case 'select':
      return selectOption(field, v)?.label ?? '';
    case 'multiSelect':
      return asStringArray(v)
        .map((id) => selectOption(field, id)?.label)
        .filter(Boolean)
        .join(', ');
    case 'date':
      return asString(v);
    case 'person':
      return asStringArray(v)
        .map((id) => ctx.personName?.(id) ?? id)
        .join(', ');
    case 'relation':
      return asStringArray(v)
        .map((id) => ctx.recordTitle?.(id) ?? id)
        .join(', ');
    case 'formula':
      return formatNumber(formulaValue(record, field, ctx), field.config);
    case 'votes':
      return String(voteScore(record));
    case 'file':
      return asFiles(v).map((f) => f.name).join(', ');
  }
}

/* ----------------------------------------------------------------------------------------------
 * Type conversion (when a user changes a field's type, keep as much data as possible)
 * -------------------------------------------------------------------------------------------- */

export function convertValue(value: unknown, from: FieldDef, to: FieldDef, record: RecordData, ctx: ValueContext = {}): unknown {
  if (value === undefined || value === null) return undefined;
  const text = valueToText(record, from, ctx);
  switch (to.type) {
    case 'text':
    case 'url':
      return text || undefined;
    case 'number':
    case 'rating': {
      const n = numericValue(record, from, ctx) ?? asNumber(text);
      return n === null ? undefined : n;
    }
    case 'range': {
      if (from.type === 'range') return value;
      const n = numericValue(record, from, ctx);
      return n === null ? undefined : [n, n];
    }
    case 'dice':
      return parseDice(text) ? text : undefined;
    case 'checkbox':
      return ['yes', 'true', '1', 'x', '✓'].includes(text.trim().toLowerCase()) || undefined;
    case 'select': {
      const label = from.type === 'multiSelect' ? text.split(', ')[0] : text;
      return to.config.options?.find((o) => o.label.toLowerCase() === (label ?? '').toLowerCase())?.id;
    }
    case 'multiSelect': {
      const labels = text.split(',').map((s) => s.trim().toLowerCase());
      const ids = (to.config.options ?? []).filter((o) => labels.includes(o.label.toLowerCase())).map((o) => o.id);
      return ids.length ? ids : undefined;
    }
    case 'date':
      return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, to.config.includeTime ? 16 : 10) : undefined;
    case 'person':
    case 'relation':
      return from.type === to.type ? value : undefined;
    case 'color':
      return /^#[0-9a-f]{3,8}$/i.test(text) ? text : undefined;
    default:
      return undefined;
  }
}

/**
 * Options for a select converted from free text: one option per distinct value.
 * Used when turning a text field into a select/tags field.
 */
export function optionsFromValues(labels: Iterable<string>, makeId: () => string, existing: SelectOption[] = []): SelectOption[] {
  const palette = ['gray', 'blue', 'green', 'orange', 'purple', 'pink', 'teal', 'yellow', 'red', 'brown'] as const;
  const out = [...existing];
  const seen = new Set(existing.map((o) => o.label.toLowerCase()));
  for (const raw of labels) {
    for (const part of raw.split(',')) {
      const label = part.trim();
      if (!label || seen.has(label.toLowerCase())) continue;
      seen.add(label.toLowerCase());
      out.push({ id: makeId(), label, color: palette[out.length % palette.length]! });
    }
  }
  return out;
}

/** Default config when creating a field of a type. */
export function defaultFieldConfig(type: FieldType): FieldConfig {
  switch (type) {
    case 'select':
    case 'multiSelect':
      return { options: [] };
    case 'rating':
      return { max: 5 };
    case 'relation':
      return { targetCollectionId: null, multiple: true };
    case 'person':
      return { multiple: true };
    case 'formula':
      return { expression: '' };
    case 'votes':
      return { allowDownvotes: false };
    default:
      return {};
  }
}
